import { Suspense, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import logo from '@/assets/pgs-logo.jpg';
import { useCan, useCurrentUser } from '@/app/hooks';
import { useLogoutMutation } from '@/features/auth/authApi';
import { useNotificationsQuery } from '@/features/messaging/messagingApi';
import { Avatar } from './Avatar';
import { Icon, type IconName } from './Icon';
import { Omnibox } from './Omnibox';
import { RouteFallback } from './Spinner';
import { PAGE_META, sectionFor } from './pageMeta';

interface NavItem {
  to: string;
  text: string;
  icon: IconName;
  count?: number;
  end?: boolean;
}

/**
 * The signed-in shell: fixed navy sidebar, sticky topbar, content.
 *
 * This replaces a single wrapping row of ~22 links. V20 grouped its
 * navigation under headings and gave each section its own identity, and staff
 * navigate by shape as much as by label — a flat list of everything a person
 * can reach is not the same product to look at.
 */
export function AppLayout() {
  const user = useCurrentUser();
  const can = useCan();
  const location = useLocation();
  const [logout] = useLogoutMutation();
  const { data: notifications } = useNotificationsQuery();
  const [menuOpen, setMenuOpen] = useState(false);

  // A drawer left open over the next page is disorienting on a phone.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const isManager = can('employee:read:reports');
  const isHr = can('employee:write:all');
  const canManageUsers = can('user:manage');
  const unread = notifications?.unread ?? 0;

  const groups: { label: string; items: NavItem[] }[] = [
    {
      label: 'My workspace',
      items: [
        { to: '/', text: 'Dashboard', icon: 'dashboard', end: true },
        { to: '/leave', text: 'My holiday & leave', icon: 'leave' },
        { to: '/calendar', text: 'Company calendar', icon: 'calendar' },
        { to: '/sickness', text: 'Sickness & absence', icon: 'medical' },
      ],
    },
    {
      label: 'My employment',
      items: [
        { to: '/contract', text: 'My contracts', icon: 'contract' },
        { to: '/handbook', text: 'Staff handbook', icon: 'book' },
        { to: '/policies', text: 'Company policies', icon: 'policy' },
        { to: '/documents', text: 'Payslips & documents', icon: 'pay' },
        { to: '/new-starter', text: 'Employment pack', icon: 'files' },
      ],
    },
    {
      label: 'Support & reward',
      items: [
        { to: '/messages', text: 'Messages', icon: 'message' },
        { to: '/notifications', text: 'Notifications', icon: 'bell', count: unread },
        { to: '/benefits', text: 'Benefits package', icon: 'gift' },
        { to: '/training', text: 'Learning & CPD', icon: 'learning' },
        { to: '/directory', text: 'Telephone directory', icon: 'phone' },
        { to: '/profile', text: 'My details', icon: 'profile' },
        { to: '/help', text: 'Help & support', icon: 'help' },
      ],
    },
  ];

  if (isManager) {
    groups.push({
      label: 'Team records',
      items: [
        { to: '/approvals', text: 'Approvals', icon: 'approve' },
        { to: '/people', text: 'Team people', icon: 'people' },
        { to: '/analytics', text: 'Absence & cost', icon: 'chart' },
      ],
    });
  }

  const hrItems: NavItem[] = [];
  if (isHr) {
    hrItems.push(
      { to: '/admin/people', text: 'Employee records', icon: 'people' },
      { to: '/admin/organisation', text: 'Departments & roles', icon: 'briefcase' },
      { to: '/admin/payroll', text: 'Payroll documents', icon: 'pay' },
      { to: '/admin/documents', text: 'Document control', icon: 'files' },
      { to: '/admin/policies', text: 'Policies', icon: 'policy' },
      { to: '/admin/contracts', text: 'Contracts', icon: 'contract' },
      { to: '/admin/benefits', text: 'Benefit schemes', icon: 'gift' },
      { to: '/admin/training', text: 'Course catalogue', icon: 'learning' },
      { to: '/admin/data', text: 'Employee CSV', icon: 'download' },
    );
  }
  if (canManageUsers) {
    hrItems.push(
      { to: '/admin/accounts', text: 'Users & invitations', icon: 'key' },
      { to: '/admin/settings', text: 'Portal controls', icon: 'settings' },
    );
  }
  if (hrItems.length > 0) groups.push({ label: 'HR administration', items: hrItems });

  const section = sectionFor(isHr, isManager);
  const meta = PAGE_META[location.pathname] ?? PAGE_META['/']!;
  const name = user?.displayName ?? '';
  const personId = user?.employeeId ?? user?.id ?? '';

  return (
    <div className="app">
      <div
        className={`mobile-backdrop${menuOpen ? ' open' : ''}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      <aside className={`sidebar${menuOpen ? ' open' : ''}`}>
        <div className="sidebar-brand">
          <img className="sidebar-logo" src={logo} alt="People Group Services" />
          <div className="sidebar-product">
            <strong>HR Portal</strong>
            <span>{section.label}</span>
          </div>
        </div>

        <nav className="sidebar-scroll" aria-label="Main">
          {groups.map((group) => (
            <div key={group.label}>
              <div className="nav-label">{group.label}</div>
              {group.items.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.end} className="nav-button">
                  <Icon name={item.icon} size={19} className="nav-icon" />
                  <span className="nav-text">{item.text}</span>
                  {item.count ? <span className="nav-count">{item.count}</span> : null}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-user">
          <div className="sidebar-user-card">
            <Avatar employeeId={personId} fullName={name} size={34} />
            <div className="sidebar-user-copy">
              <strong>{name}</strong>
              <span>{section.roleLabel}</span>
            </div>
            <button
              className="sidebar-logout"
              onClick={() => void logout()}
              title="Sign out"
              aria-label="Sign out"
            >
              <Icon name="logout" size={16} />
            </button>
          </div>
        </div>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <div className="topbar-left">
            <button
              className="menu-button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Open menu"
              aria-expanded={menuOpen}
            >
              <Icon name="menu" size={18} />
            </button>
            <div className="page-title">
              <span className={`section-chip ${section.chipClass}`}>{section.label}</span>
              <h1>{meta[0]}</h1>
              <p>{meta[1]}</p>
            </div>
          </div>

          <div className="topbar-right">
            <Omnibox />
            <NavLink to="/notifications" className="notification-button" aria-label="Notifications">
              <Icon name="bell" size={18} />
              {unread > 0 ? <span className="notification-dot">{unread}</span> : null}
            </NavLink>
            <NavLink to="/profile" className="top-user">
              <Avatar employeeId={personId} fullName={name} size={34} />
              <span className="top-user-copy">
                <strong>{name}</strong>
                <span>{section.roleLabel}</span>
              </span>
            </NavLink>
          </div>
        </header>

        <main className="content">
          {/* Each route below is a separate chunk; this is what the user sees
              while it downloads. */}
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>

          <p className="content-foot">
            Sensitive pay, bank, medical and right-to-work data is restricted by role. Uploads are
            not virus-scanned and records are not encrypted at rest — see Help for what is and is
            not in place.
          </p>
        </main>
      </div>
    </div>
  );
}
